/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from "react";
import { useState, useEffect } from "react";
import ReactAudioPlayer from "react-audio-player";
import ReactPlayer from "react-player";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";

import { Button } from "../../components/ui/button";
import { ChevronLeft } from "lucide-react";
import VideoCover from "../../video-cover.jpg";
import { getFileType } from "../../helper/function";
import { services } from "../../services";
import { actions } from "../../actions";
import CustomLoader from "../ui/custom-loader";
import { cn } from "../../lib/utils";

interface RenderNFTProps {
  image?: {
    format?: string;
    compressed?: string;
    original?: string;
  };
}

const RenderNFT: React.FC<RenderNFTProps> = (record) => {
  const [ext, setExt] = useState<string | false>(false);

  useEffect(() => {
    async function getExtenstion(): Promise<void> {
      if (!record?.image?.format) {
        const extenstion = await getFileType(record?.image?.compressed);
        setExt(extenstion);
      } else {
        setExt(record.image.format);
      }
    }
    getExtenstion();
  }, [record]);

  if (ext && ext === "image")
    return (
      <img
        src={record.image?.original}
        alt="Asset image"
        className="h-[250px] w-[250px] object-contain rounded-lg"
      />
    );
  if (ext && ext === "audio")
    return <ReactAudioPlayer src={record.image?.compressed} controls />;
  if (ext && ext === "video")
    return (
      <ReactPlayer
        controls={true}
        url={record.image?.original}
        playIcon={<></>}
        width="450px"
        height="250px"
        light={VideoCover}
      />
    );
  return (
    <div>
      <CustomLoader size="lg" />
    </div>
  );
};

const RentalShow = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState<boolean>(true);
  const [record, setRecord] = useState<any>(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Replace with your actual API call
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/rental/property/${id}`,
        );
        const data = await response.json();
        setRecord(data.data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    const slider = document.querySelector(".snap-x");
    if (slider) {
      const handleScroll = () => {
        const slideWidth = slider.clientWidth;
        const newSlide = Math.round(slider.scrollLeft / slideWidth);
        setCurrentSlide(newSlide);
      };

      slider.addEventListener("scroll", handleScroll);
      return () => slider.removeEventListener("scroll", handleScroll);
    }
  }, []);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <CustomLoader size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-2xl font-bold">Rental Details</h2>
      </div>

      <Card className="shadow-lg">
        <CardContent className="p-6">
          <Tabs defaultValue="asset-info">
            <TabsList className="flex justify-start border-b mb-6">
              <TabsTrigger value="asset-info">Asset Info</TabsTrigger>
              <TabsTrigger value="transaction">Transaction Details</TabsTrigger>
            </TabsList>

            <TabsContent value="asset-info" className="space-y-4">
              <div className="grid gap-4">
                <div className="bg-gray-50/50 dark:bg-gray-800/50 rounded-lg p-4 transition-colors">
                  <h4 className="text-sm font-normal text-gray-500 dark:text-gray-400 mb-1">
                    Asset Name
                  </h4>
                  <p className="text-lg font-medium">
                    {record?.rentalProperty.assetId.assetName}
                  </p>
                </div>

                <div className="bg-gray-50/50 dark:bg-gray-800/50 rounded-lg p-4 transition-colors">
                  <h4 className="text-sm font-normal text-gray-500 dark:text-gray-400 mb-1">
                    Asset Image
                  </h4>
                  <div className="flex flex-col gap-4">
                    <div className="relative max-w-[600px] mx-0">
                      <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                        <div className="flex snap-x snap-mandatory overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] relative">
                          <div className="snap-center shrink-0 w-full flex flex-col">
                            <div className="relative h-[250px] w-full flex justify-center items-center">
                              <img
                                src={
                                  record?.rentalProperty?.assetId?.image
                                    ?.original
                                }
                                alt="Main Asset Image"
                                className="h-full max-w-full object-contain rounded-lg"
                              />
                            </div>
                            <div className="p-2 text-center bg-gray-50 dark:bg-gray-800">
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                Main Image
                              </p>
                            </div>
                          </div>

                          {record?.rentalProperty?.assetId?.image?.sideImages &&
                            record?.rentalProperty?.assetId?.image.sideImages.map(
                              (sideImage, index) => (
                                <div
                                  key={index}
                                  className="snap-center shrink-0 w-full flex flex-col"
                                >
                                  <div className="relative h-[250px] w-full flex justify-center items-center">
                                    <img
                                      src={sideImage.url}
                                      alt={
                                        sideImage.description ||
                                        `Side image ${index + 1}`
                                      }
                                      className="h-full max-w-full object-contain rounded-lg"
                                    />
                                  </div>
                                  {sideImage.description && (
                                    <div className="p-2 text-center bg-gray-50 dark:bg-gray-800">
                                      <p className="text-sm text-gray-600 dark:text-gray-400">
                                        {sideImage.description}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              ),
                            )}
                        </div>

                        <button
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 dark:bg-gray-800/80 rounded-full p-2 shadow-md hover:bg-white dark:hover:bg-gray-800 focus:outline-none z-10"
                          onClick={() => {
                            const slider = document.querySelector(".snap-x");
                            if (slider) {
                              const slideWidth = slider.clientWidth;
                              const prevSlide = currentSlide - 1;
                              const targetSlide = prevSlide < 0 ? 0 : prevSlide;
                              slider.scrollTo({
                                left: slideWidth * targetSlide,
                                behavior: "smooth",
                              });
                              setCurrentSlide(targetSlide);
                            }
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>

                        <button
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 dark:bg-gray-800/80 rounded-full p-2 shadow-md hover:bg-white dark:hover:bg-gray-800 focus:outline-none z-10"
                          onClick={() => {
                            const slider = document.querySelector(".snap-x");
                            if (slider) {
                              const slideWidth = slider.clientWidth;
                              const totalSlides =
                                1 +
                                (record?.rentalProperty?.assetId?.image
                                  ?.sideImages?.length || 0);
                              const nextSlide = currentSlide + 1;
                              const targetSlide =
                                nextSlide >= totalSlides
                                  ? totalSlides - 1
                                  : nextSlide;
                              slider.scrollTo({
                                left: slideWidth * targetSlide,
                                behavior: "smooth",
                              });
                              setCurrentSlide(targetSlide);
                            }
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>

                      <div className="flex justify-center mt-2 gap-1">
                        {Array.from({
                          length:
                            1 +
                            (record?.rentalProperty?.assetId?.image?.sideImages
                              ?.length || 0),
                        }).map((_, index) => (
                          <button
                            key={index}
                            className={`w-2 h-2 rounded-full focus:outline-none transition-colors ${
                              index === currentSlide
                                ? "bg-primary dark:bg-primary"
                                : "bg-gray-300 dark:bg-gray-600"
                            }`}
                            onClick={() => {
                              const slider = document.querySelector(".snap-x");
                              if (slider) {
                                const slideWidth = slider.clientWidth;
                                slider.scrollTo({
                                  left: slideWidth * index,
                                  behavior: "smooth",
                                });
                                setCurrentSlide(index);
                              }
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50/50 dark:bg-gray-800/50 rounded-lg p-4 transition-colors">
                  <h4 className="text-sm font-normal text-gray-500 dark:text-gray-400 mb-1">
                    Rental Price
                  </h4>
                  <p className="text-lg font-medium">
                    {record?.rentalProperty?.totalRentalPrice}
                  </p>
                </div>
                <div className="bg-gray-50/50 dark:bg-gray-800/50 rounded-lg p-4 transition-colors">
                  <h4 className="text-sm font-normal text-gray-500 dark:text-gray-400 mb-1">
                    End Date
                  </h4>
                  <p className="text-lg font-medium">
                    {new Date(
                      record?.rentalProperty?.endDate,
                    ).toLocaleDateString()}
                  </p>
                </div>
                <div className="bg-gray-50/50 dark:bg-gray-800/50 rounded-lg p-4 transition-colors">
                  <h4 className="text-sm font-normal text-gray-500 dark:text-gray-400 mb-1">
                    Description
                  </h4>
                  <p className="text-base leading-relaxed">
                    {
                      record?.rentalProperty.assetId.AssetDetials.detials
                        .description
                    }
                  </p>
                </div>
                <div className="bg-gray-50/50 dark:bg-gray-800/50 rounded-lg p-4 transition-colors">
                  <h4 className="text-sm font-normal text-gray-500 dark:text-gray-400 mb-1">
                    Total Minted Tokens
                  </h4>
                  <p className="text-lg font-medium">
                    {record?.rentalProperty.assetId.maxSupply}
                  </p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="transaction" className="space-y-4">
              <div className="grid gap-4">
                <div className="bg-gray-50/50 dark:bg-gray-800/50 rounded-lg p-4 transition-colors">
                  <h4 className="text-sm font-normal text-gray-500 dark:text-gray-400 mb-1">
                    Asset Address
                  </h4>
                  <p className="break-all font-mono text-sm">
                    {record?.rentalProperty?.assetAddress}
                  </p>
                </div>
                <div className="bg-gray-50/50 dark:bg-gray-800/50 rounded-lg p-4 transition-colors">
                  <h4 className="text-sm font-normal text-gray-500 dark:text-gray-400 mb-1">
                    Transaction Hash
                  </h4>
                  <p className="break-all font-mono text-sm">
                    {record?.rentalProperty?.listRentaltxHash}
                  </p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default RentalShow;
